import CaseType from "../../entities/caseType";

/**
 * CaseType actions
 *
 * Call order:
 *
 * CASETYPE_SAVE_QUEUE:	adds the item to the save queue
 * --> CASETYPE_SAVE:	modifies the state
 * --> CASETYPE_SAVE_SUCCESS: if all went well
 * --> CASETYPE_SAVE_FAILURE:	if either the queue or local save failed
 *
 * Note that the only way to fail CASETYPE_SAVE_QUEUE is during the call to CASETYPE_SAVE,
 * as adding an item to the queue should never fail.
 *
 * OWA-1018: the reducer shows a queued save at CASETYPE_SAVE_QUEUE, marked pending, and only that
 * save's own outcome settles the mark: CASETYPE_SAVE_SUCCESS, CASETYPE_SAVE_REVERT (refused, or
 * never queued), a refusal the queue never retries, or CASETYPE_PENDING_CHECK (it left the queue).
 */

export const CASETYPE_FETCH_ONE_QUEUE: any = 'CASETYPE_FETCH_ONE_QUEUE';
export const CASETYPE_FETCH_SUCCESS: any = 'CASETYPE_FETCH_SUCCESS';
export const CASETYPE_FETCH_FAILURE: any = 'CASETYPE_FETCH_FAILURE';
export const CASETYPE_FETCH_ONE_SUCCESS: any = 'CASETYPE_FETCH_ONE_SUCCESS';
export const CASETYPE_FETCH_ONE_FAILURE: any = 'CASETYPE_FETCH_ONE_FAILURE';

export const CASETYPE_SAVE: any = 'CASETYPE_SAVE';
export const CASETYPE_SAVE_QUEUE: any = 'CASETYPE_SAVE_QUEUE';
export const CASETYPE_SAVE_SUCCESS: any = 'CASETYPE_SAVE_SUCCESS';
export const CASETYPE_SAVE_FAILURE: any = 'CASETYPE_SAVE_FAILURE';

export const CASETYPE_SAVE_MANY: any = 'CASETYPE_SAVE_MANY';
export const CASETYPE_SAVE_MANY_QUEUE: any = 'CASETYPE_SAVE_MANY_QUEUE';
export const CASETYPE_SAVE_MANY_SUCCESS: any = 'CASETYPE_SAVE_MANY_SUCCESS';
export const CASETYPE_SAVE_MANY_FAILURE: any = 'CASETYPE_SAVE_MANY_FAILURE';

export const CASETYPE_REMOVE: any = 'CASETYPE_REMOVE';
export const CASETYPE_REMOVE_QUEUE: any = 'CASETYPE_REMOVE_QUEUE';
export const CASETYPE_REMOVE_SUCCESS: any = 'CASETYPE_REMOVE_SUCCESS';
export const CASETYPE_REMOVE_FAILURE: any = 'CASETYPE_REMOVE_FAILURE';

export const CASETYPE_RESTORE: any = 'CASETYPE_RESTORE';
export const CASETYPE_RESTORE_QUEUE: any = 'CASETYPE_RESTORE_QUEUE';
export const CASETYPE_RESTORE_SUCCESS: any = 'CASETYPE_RESTORE_SUCCESS';
export const CASETYPE_RESTORE_FAILURE: any = 'CASETYPE_RESTORE_FAILURE';

export const CASETYPE_RESTORE_MANY: any = 'CASETYPE_RESTORE_MANY';
export const CASETYPE_REMOVE_MANY: any = 'CASETYPE_REMOVE_MANY';

// OWA-1018: settle this tab's pending save of a record
export const CASETYPE_SAVE_REVERT: any = 'CASETYPE_SAVE_REVERT';
export const CASETYPE_PENDING_CHECK: any = 'CASETYPE_PENDING_CHECK';

/* Fetch actions */
export const fetchCaseTypeSuccess = (caseTypes: CaseType[], partial: boolean, lastFetch: number) => ({type: CASETYPE_FETCH_SUCCESS, caseTypes, partial, lastFetch});
export const fetchCaseTypeFailure = (err: any) => {
		console.error('CaseTypeAction: fetchCaseTypeFailure', err);
		return {type: CASETYPE_FETCH_FAILURE, err};
};
export const fetchOneCaseTypeQueue = (caseTypeId: string, sentAddedOn?: number) => ({type: CASETYPE_FETCH_ONE_QUEUE, caseTypeId, sentAddedOn});
export const fetchOneCaseTypeSuccess = (caseType: CaseType | null, sentAddedOn?: number, caseTypeId: string = caseType?.id) => ({type: CASETYPE_FETCH_ONE_SUCCESS, caseTypeId, caseType, sentAddedOn});
export const fetchOneCaseTypeFailure = (id: string, err: any) => {
		console.error('CaseTypeAction: fetchOneCaseTypeFailure', err);
		return {type: CASETYPE_FETCH_ONE_FAILURE, id, err};
};

/* Save actions */
export const saveCaseType = (caseType: CaseType) => {
    // console.debug('⮂ CASETYPE_SAVE action', caseType?.id);
    return {type: CASETYPE_SAVE, caseType};
}
export const saveCaseTypeQueue = (caseType: CaseType, onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ CASETYPE_SAVE_QUEUE action', caseType?.id);
    return {type: CASETYPE_SAVE_QUEUE, caseType, onQueued};
}
export const saveCaseTypeSuccess = (caseType: CaseType, sentAddedOn?: number) => {
    // console.debug('⮂ CASETYPE_SAVE_SUCCESS action', caseType?.id);
    return {type: CASETYPE_SAVE_SUCCESS, caseType, sentAddedOn};
}
export const saveCaseTypeFailure = (caseType: CaseType, status: number, message: string) => {
    console.warn(`⮂ CASETYPE_SAVE_FAILURE error saving caseType: HTTP ${status}: ${message}`, caseType?.id);
    return {type: CASETYPE_SAVE_FAILURE, caseType, status, message};
};

/* SaveMany actions */
export const saveManyCaseTypes = (caseTypes: CaseType[]) => {
    // console.debug('⮂ CASETYPE_SAVE_MANY action', caseTypes?.map(caseType => caseType?.id).join(','));
    return {type: CASETYPE_SAVE_MANY, caseTypes};
}
export const saveManyCaseTypesQueue = (caseTypes: CaseType[], onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ CASETYPE_SAVE_MANY_QUEUE action', caseTypes?.map(caseType => caseType.id).join(','));
    return {type: CASETYPE_SAVE_MANY_QUEUE, caseTypes, onQueued};
}

/** `sentAddedOns`: the queue key each answer acknowledges, aligned with `caseTypes` (OWA-1018) */
export const saveManyCaseTypesSuccess = (caseTypes: CaseType[], sentAddedOns?: (number | undefined)[]) => {
    // console.debug('⮂ CASETYPE_SAVE_MANY_SUCCESS action', caseTypes?.map(caseType => caseType.id).join(','));
    return {type: CASETYPE_SAVE_MANY_SUCCESS, caseTypes, sentAddedOns};
}
export const saveManyCaseTypesFailure = (caseTypes: CaseType[], status: number, message: string) => {
		console.warn(`⮂ SAVE_MANY_CASETYPES_FAILURE Error saving caseTypes: HTTP ${status}: ${message}`, caseTypes?.map(caseType => caseType.id).join(','));
		return {type: CASETYPE_SAVE_MANY_FAILURE, caseTypes, status, message};
};

/* Remove actions */
export const removeCaseType = (caseType: CaseType) => ({ type: CASETYPE_REMOVE, caseType });
export const removeCaseTypeQueue = (caseType: CaseType) => ({ type: CASETYPE_REMOVE_QUEUE, caseType });
export const removeCaseTypeSuccess = (caseType: CaseType) => ({ type: CASETYPE_REMOVE_SUCCESS, caseType });
export const removeCaseTypeFailure = (caseType: CaseType, status: number, message: string) => {
	console.error(`Redux: removeCaseTypeFailure Error removing caseType: HTTP ${status}: ${message}`);
	return ({ type: CASETYPE_REMOVE_FAILURE, caseType, status, message });
};

/* Restore actions */
export const restoreCaseType = (caseType: CaseType) => ({ type: CASETYPE_RESTORE, caseType });
export const restoreCaseTypeQueue = (caseType: CaseType) => ({ type: CASETYPE_RESTORE_QUEUE, caseType });
export const restoreCaseTypeSuccess = (caseType: CaseType, sentAddedOn?: number) => ({ type: CASETYPE_RESTORE_SUCCESS, caseType, sentAddedOn });
export const restoreCaseTypeFailure = (caseType: CaseType, status: number, message: string) => {
	console.error(`Redux: restoreCaseTypeFailure Error restoring caseType: HTTP ${status}: ${message}`);
	return ({ type: CASETYPE_RESTORE_FAILURE, caseType, status, message });
};

/* RestoreMany actions */
export const restoreManyCaseTypes = (caseTypes: CaseType[]) => ({ type: CASETYPE_RESTORE_MANY, caseTypes });

/* RemoveMany actions */
export const removeManyCaseTypes = (caseTypes: CaseType[]) => ({ type: CASETYPE_REMOVE_MANY, caseTypes });

/* Pending save actions (OWA-1018) */
/**
 * The save `caseType` (queue key `sentAddedOn`, default its addedOn) will not be accepted: it was
 * refused, or never reached the queue. Its row returns to the latest accepted version, or to
 * `current` (the record as the server holds it) when that is newer; `keep` leaves the row shown.
 */
export const revertCaseTypeSave = (caseType: CaseType, options: {sentAddedOn?: number, current?: CaseType | null, keep?: boolean} = {}) =>
    ({type: CASETYPE_SAVE_REVERT, caseType, ...options});
/** processQueue read the shared queue at `readAt` (performance.now()): the live and the permanently refused queue keys */
export const checkCaseTypePendingSaves = (live: number[], refused: number[], readAt: number) =>
    ({type: CASETYPE_PENDING_CHECK, live, refused, readAt});